import React, { useEffect, useState } from "react";
import { X, Lock, Mail, Phone, Linkedin, Globe, Loader2, CheckCircle2, AlertCircle, HelpCircle, ExternalLink, Copy, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { EmailComposer } from "@/components/EmailComposer";
import { EnrichButton } from "@/components/EnrichButton";
import { api, errorCode, errorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, MatchBadge } from "@/components/CandidateCard";
import { Pill } from "@/components/common";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RichSummary } from "@/components/RichSummary";
import { useI18n } from "@/lib/i18n";
import { tk } from "@/i18n";
import { tDynamic } from "@/i18n/dynamic";
import { fmtShortDate, fmtDateTime } from "@/i18n/format";
import { RecruiterInfo, CandidateFiles } from "@/components/RecruiterInfo";
import SaveToDialog from "@/components/SaveToDialog";
import { FolderPlus } from "lucide-react";

const ICONS = { professional_email: Mail, personal_email: Mail, phone: Phone, linkedin: Linkedin, github: Globe, website: Globe };
const PROV = { verified_by_candidate: ["green", tk("Verified by candidate")], verified_by_source: ["green", tk("Verified by source")], inferred: ["indigo", tk("Inferred")], unverified: ["default", tk("Unverified")], unknown: ["default", tk("Unknown")] };
const lbl = (t, s) => t(String(s || "").replaceAll("_", " "));

function LocalContactEditor({ cand, onSaved }) {
  const { t } = useI18n();
  const cur = (k) => cand.contacts.find((c) => c.local && c.type === k)?.value || "";
  const [f, setF] = useState({ email: cur("professional_email"), phone: cur("phone") });
  const [open, setOpen] = useState(false);
  const save = async () => {
    try { await api.put(`/candidates/${cand.id}/local-contacts`, f); toast.success(t("Saved in your catalog")); setOpen(false); onSaved(); } catch (e) { toast.error(errorDetail(e)); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="text-xs text-primary hover:underline" data-testid="edit-local-contact">{t("Add or update email / phone")}</button>;
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="local-contact-form">
      <p className="text-[11px] text-muted-foreground">{t("A private copy is kept in your organization's catalog only.")}</p>
      <input className="w-full h-8 rounded-md border bg-background px-2 text-sm" type="email" placeholder={t("Email")} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="local-contact-email" />
      <input className="w-full h-8 rounded-md border bg-background px-2 text-sm" placeholder={t("Phone")} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="local-contact-phone" />
      <div className="flex gap-2"><Button size="sm" onClick={save} data-testid="local-contact-save">{t("Save")}</Button><Button size="sm" variant="ghost" onClick={() => setOpen(false)}>{t("Cancel")}</Button></div>
    </div>
  );
}

function ContactRow({ cand, ct }) {
  const { setUpgradeOpen, user } = useAuth();
  const { t } = useI18n();
  const [value, setValue] = useState(ct.value || null);
  const [loading, setLoading] = useState(false);
  const Icon = ICONS[ct.type] || Globe;
  const reveal = async () => {
    if (!user?.entitlements?.can_reveal_contacts) return setUpgradeOpen(true);
    setLoading(true);
    try {
      const { data } = await api.post(`/candidates/${cand.id}/contacts/${ct.id}/reveal`);
      setValue(data.value);
    } catch (e) {
      if (errorCode(e) === "upgrade_required") setUpgradeOpen(true); else toast.error(errorDetail(e));
    } finally { setLoading(false); }
  };
  return (
    <div className="flex items-center gap-2 rounded-lg border px-3 py-2" data-testid={`contact-row-${ct.type}`}>
      <Icon className="w-4 h-4 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-muted-foreground">{lbl(t, ct.type)} · {lbl(t, ct.verification_status)}{ct.confidence ? ` · ${t("confidence: {{level}}", { level: lbl(t, ct.confidence) })}` : ""} · {lbl(t, ct.source)}</div>
        {value ? <div className="text-sm font-mono truncate" data-testid="revealed-contact-value">{value}</div>
          : <button onClick={reveal} data-testid={`reveal-contact-${ct.type}`} className="text-sm font-medium text-primary flex items-center gap-1 hover:underline">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
            {ct.type.includes("email") ? t("Professional email available — reveal") : t("Available — reveal")}
          </button>}
      </div>
      {value && <button onClick={() => { navigator.clipboard.writeText(value); toast.success(t("Copied")); }} aria-label={t("Copy")} data-testid="copy-contact"><Copy className="w-3.5 h-3.5 text-muted-foreground" /></button>}
    </div>
  );
}

function Section({ title, children }) {
  return <section className="space-y-2"><div className="eyebrow">{title}</div>{children}</section>;
}

export default function CandidatePanel({ id, match, onClose }) {
  const { t } = useI18n();
  const [c, setC] = useState(null);
  const [note, setNote] = useState("");
  const [compose, setCompose] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [convs, setConvs] = useState([]);
  const reloadCandidate = () => api.get(`/candidates/${id}`).then((r) => setC(r.data)).catch(() => {});
  useEffect(() => {
    setC(null);
    api.get(`/candidates/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(errorDetail(e)));
    api.get("/mail/conversations", { params: { candidate_id: id } }).then((r) => setConvs(r.data)).catch(() => {});
  }, [id]);
  const addNote = async () => {
    try {
      const { data } = await api.post(`/candidates/${id}/notes`, { body: note });
      setC({ ...c, notes: [data, ...c.notes] });
      setNote("");
    } catch (e) { toast.error(errorDetail(e)); }
  };
  if (!c) return <div className="p-6 space-y-3" data-testid="candidate-panel-loading">{[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-lg shimmer bg-muted" />)}</div>;
  return (
    <div className="p-5 space-y-6" data-testid="candidate-panel">
      <div className="flex items-start gap-3">
        <Avatar name={c.full_name} size={12} />
        <div className="flex-1 min-w-0">
          <div className="text-lg font-bold leading-tight" data-testid="panel-candidate-name">{c.full_name}</div>
          <div className="text-sm text-muted-foreground">{c.current_title}{c.current_company ? ` · ${c.current_company}` : ""}</div>
          <div className="flex flex-wrap gap-1 mt-2">
            <Pill tone={PROV[c.verification_status]?.[0]}>{t(PROV[c.verification_status]?.[1] || c.verification_status)}</Pill>
            <Pill>{c.scope === "private" ? t("Private pool") : t("Global catalog")}</Pill>
            {c.data_freshness_at && <Pill>{t("Updated {{date}}", { date: fmtShortDate(c.data_freshness_at) })}</Pill>}
            {c.availability !== "unknown" && <Pill tone="indigo">{t("Declared: {{availability}}", { availability: lbl(t, c.availability) })}</Pill>}
          </div>
        </div>
        <button onClick={onClose} data-testid="close-candidate-panel" aria-label={t("Close")} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
      </div>

      {match && (
        <Section title={t("AI match insight")}>
          <div className="rounded-xl border p-3 space-y-2 bg-accent/30" data-testid="match-explanation">
            <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground">{tDynamic(match.notice)}</span><MatchBadge score={match.score} coverage={match.coverage} /></div>
            {match.supported.map((s, i) => <div key={i} className="flex gap-2 text-xs"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" /><span><b>{tDynamic(s.criterion)}</b> — {tDynamic(s.evidence)} <span className="text-muted-foreground">({lbl(t, s.provenance)})</span></span></div>)}
            {match.gaps_to_verify.map((s, i) => <div key={i} className="flex gap-2 text-xs"><AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />{tDynamic(s)}</div>)}
            {match.missing_information.map((s, i) => <div key={i} className="flex gap-2 text-xs text-muted-foreground"><HelpCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{tDynamic(s)}</div>)}
          </div>
        </Section>
      )}

      <Section title={t("Contact")}>
        {c.contacts.length ? c.contacts.map((ct) => <ContactRow key={`${ct.id}:${ct.value || ""}`} cand={c} ct={ct} />) : <p className="text-sm text-muted-foreground">{t("No permitted contact on record.")}</p>}
        <LocalContactEditor key={c.contacts.map((x) => x.value || "").join("|")} cand={c} onSaved={reloadCandidate} />
        <div className="flex flex-wrap gap-2"><Button size="sm" variant="outline" onClick={() => setCompose(true)} data-testid="email-candidate-button"><Send className="w-4 h-4" />{t("Contact candidate")}</Button>
          <Button size="sm" variant="outline" onClick={() => setSaveOpen(true)} data-testid="panel-add-to-project"><FolderPlus className="w-4 h-4" />{t("Add to a project or list")}</Button></div>
        <SaveToDialog candidate={saveOpen ? c : null} onClose={() => setSaveOpen(false)} />
        {c.profile_url && <div className="flex flex-wrap items-center gap-2">
          <EnrichButton candidateId={c.id} enrichment={c.enrichment_email} kind="email" onDone={setC} testid="panel-enrich-email-button" />
          <EnrichButton candidateId={c.id} enrichment={c.enrichment_phone} kind="phone" onDone={setC} testid="panel-enrich-phone-button" />
          <span className="text-[11px] text-muted-foreground w-full">{t("Credits refunded if nothing is found")}</span></div>}
        {convs.map((cv) => <Link key={cv.id} to="/app/messages" className="block text-xs rounded-md bg-muted px-2.5 py-1.5" data-testid={`candidate-conversation-${cv.id}`}><b>{cv.subject}</b> · {lbl(t, cv.status)} · {cv.owner_name}</Link>)}
      </Section>
      <EmailComposer candidate={c} open={compose} onOpenChange={setCompose} onSent={(cv) => setConvs([cv, ...convs])} />

      {c.summary && <Section title={t("Summary")}><RichSummary text={c.summary} /></Section>}

      {(c.recruiter_info || c.tags?.length > 0) && <Section title={t("Recruiter information")}><RecruiterInfo c={c} /></Section>}
      <CandidateFiles id={c.id} />

      <Section title={t("Experience")}>
        {c.experiences.length ? (
          <ol className="relative border-l ml-1.5 space-y-4">
            {c.experiences.map((e) => (
              <li key={e.id} className="ml-4">
                <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full bg-primary/70 border-2 border-background mt-1.5" />
                <div className="text-sm font-semibold">{e.title}</div>
                <div className="text-xs text-muted-foreground">{e.company}{e.start_date ? ` · ${e.start_date.slice(0, 7)} – ${e.is_current ? t("present") : (e.end_date || "").slice(0, 7)}` : ""}</div>
                {e.description && <p className="text-xs mt-1">{e.description}</p>}
              </li>
            ))}
          </ol>
        ) : <p className="text-sm text-muted-foreground">{t("Experience not documented.")}</p>}
      </Section>

      <Section title={t("Skills")}>
        <div className="flex flex-wrap gap-1">{c.skills.map((s) => <span key={s.name} title={lbl(t, s.provenance)} className={`text-xs rounded-md border px-2 py-0.5 ${s.provenance.startsWith("verified") ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-950" : ""}`}>{s.name}</span>)}</div>
      </Section>

      {c.education.length > 0 && <Section title={t("Education")}>{c.education.map((e) => <div key={e.id} className="text-sm"><b>{e.institution}</b>{e.degree ? ` — ${e.degree}` : ""}{e.field_of_study ? `, ${e.field_of_study}` : ""}</div>)}</Section>}

      {c.languages.length > 0 && <Section title={t("Languages")}><div className="flex gap-1">{c.languages.map((l) => <Pill key={l.code}>{l.code.toUpperCase()}{l.level ? ` · ${l.level}` : ""}</Pill>)}</div></Section>}

      <Section title={t("Sources & provenance")}>
        {c.sources.map((s, i) => (
          <div key={i} className="text-xs flex items-center justify-between rounded-md bg-muted px-2.5 py-1.5" data-testid="source-row">
            <span>{lbl(t, s.type)} · {lbl(t, s.rights_basis)}</span>
            {s.url ? <a href={s.url} target="_blank" rel="noreferrer" className="text-primary flex items-center gap-1">{t("open")} <ExternalLink className="w-3 h-3" /></a> : <span className="text-muted-foreground">{fmtShortDate(s.retrieved_at)}</span>}
          </div>
        ))}
      </Section>

      <Section title={t("Team notes")}>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("Add a note visible to your organization…")} data-testid="note-input" rows={2} />
        <Button size="sm" variant="outline" disabled={!note.trim()} onClick={addNote} data-testid="add-note-button">{t("Add note")}</Button>
        {c.notes.map((n) => <div key={n.id} className="text-xs rounded-md border px-2.5 py-2"><div>{n.body}</div><div className="text-muted-foreground mt-1">{n.author} · {fmtDateTime(n.created_at)}</div></div>)}
      </Section>
    </div>
  );
}
